import type { ArrivalMenuState, ArrivalReason } from './types';
import { ARRIVAL_REASON, HELD_STATE, I9_STATE } from './types';

/** The one thing composition reads off a row of the inventory: which reason it offers. */
export interface ArrivalMenuRow {
  readonly reason: ArrivalReason;
}

/**
 * Whether a row survives the record — **ONE predicate per conditional row** [KID-5a § Handover contract].
 *
 * The three FIXED rows — drug test, pay card, ask for help — have no predicate at all: nothing is read to
 * decide them [0265]. The `held` predicates are KID-5b's and the `i9` one is KID-5c's, and every one of them
 * is a NEGATIVE comparison against a positive statement, so `unknown` — and any value nobody has seen
 * before — hides nothing [KID-5b § *The rule, stated once*: fail OPEN].
 */
function isOffered(reason: ArrivalReason, { held, i9 }: ArrivalMenuState): boolean {
  switch (reason) {
    // Only a placement gets the assignment card. A submission has no assignment to reconfirm yet.
    case ARRIVAL_REASON.reconfirmAssignment:
      return held !== HELD_STATE.submission && held !== HELD_STATE.none;
    // Only somebody with something on file, and not already placed, has an application to look at.
    case ARRIVAL_REASON.seeMyApplications:
      return held !== HELD_STATE.placement && held !== HELD_STATE.none;
    // BR-K6: an actively-placed associate is never shown other openings.
    case ARRIVAL_REASON.applyForJob:
      return held !== HELD_STATE.placement;
    // Only a positive statement that the work is done hides the I-9 step [KID-5c].
    case ARRIVAL_REASON.onboardingPaperwork:
      return i9 !== I9_STATE.complete;
    // The three FIXED rows: nothing is read to decide them [0265].
    case ARRIVAL_REASON.drugTest:
    case ARRIVAL_REASON.payCard:
    case ARRIVAL_REASON.somethingElse:
      return true;
    default:
      // A new reason fails to compile here until its condition is decided — never offered by default.
      return unreachable(reason);
  }
}

function unreachable(reason: never): never {
  throw new TypeError(`an arrival reason with no decided condition: ${String(reason)}`);
}

/**
 * **What the candidate sees: the declared inventory, minus what their record removes, with a placement's
 * `Reconfirm my assignment information` FIRST** [KID-5b; 0175; 0198].
 *
 * One pass over `inventory`, which keeps the inventory's relative order [KID-5a § Handover contract; 0205].
 *
 * ⭐ **Then the ONE re-ordering any story may apply to that inventory, and it is KID-5b's**: with a
 * placement, `reconfirmAssignment` leads [0198; Operator 2026-09-10: *«Si tengo un placement el de
 * "Reconfirm my assignment information" se muestra primero»*]. It is applied HERE, explicitly, rather than
 * inherited from the reconfirm row happening to sit first in the screen's constant — a reorder of that
 * constant for any other reason (a design tidy-up, a new row) used to break this rule silently [KID-5b §
 * *With a placement, Reconfirm renders FIRST*: *"It must be asserted, not inherited from `OPTIONS[0]`"*].
 * The rest keep their relative order; nothing else moves, and nothing here knows how many rows there are.
 *
 * Generic over the row so the screen's own option type passes through unchanged.
 *
 * Complexity: O(n) in the size of the inventory.
 */
export function composeArrivalMenu<TRow extends ArrivalMenuRow>(
  inventory: readonly TRow[],
  state: ArrivalMenuState,
): TRow[] {
  const offered = inventory.filter((row) => isOffered(row.reason, state));
  if (state.held !== HELD_STATE.placement) return offered;

  const leads = (row: TRow): boolean => row.reason === ARRIVAL_REASON.reconfirmAssignment;
  return [...offered.filter(leads), ...offered.filter((row) => !leads(row))];
}
